import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { LOGO_URL } from "@/data/site";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
      } catch {}
      attempts += 1;
      if (attempts < 8) setTimeout(poll, 2000);
      else setStatus("pending");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="bg-[#050507] min-h-screen flex flex-col" data-testid="payment-success-page">
      <header className="glass border-b border-white/10">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-20 flex items-center">
          <Link to="/" className="flex items-center gap-3" data-testid="payment-logo-link">
            <img src={LOGO_URL} alt="AirDroneJet" className="h-9 w-auto" />
            <span className="font-heading font-extrabold tracking-tight">AIR<span className="fire-text">DRONE</span>JET</span>
          </Link>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-5">
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-md w-full bg-[#13141B] border border-white/10 rounded-3xl p-10 text-center"
        >
          {status === "checking" && (
            <div data-testid="payment-checking">
              <Loader2 size={44} className="text-orange-500 mx-auto mb-5 animate-spin" />
              <h1 className="font-heading text-2xl font-extrabold text-white">Verifica pagamento...</h1>
              <p className="text-sm text-slate-400 mt-3">Stiamo confermando la transazione con Stripe.</p>
            </div>
          )}
          {status === "paid" && (
            <div data-testid="payment-paid">
              <CheckCircle2 size={52} className="text-emerald-400 mx-auto mb-5" />
              <h1 className="font-heading text-2xl font-extrabold text-white">Pagamento <span className="fire-text">riuscito!</span></h1>
              <p className="text-sm text-slate-400 mt-3 leading-relaxed">
                La tua prenotazione è confermata e pagata. Ti contatteremo per fissare l'intervento
                e dopo il lavoro troverai il report foto/video nella tua Area Clienti.
              </p>
            </div>
          )}
          {(status === "pending" || status === "error") && (
            <div data-testid="payment-pending">
              <Loader2 size={44} className="text-amber-400 mx-auto mb-5" />
              <h1 className="font-heading text-2xl font-extrabold text-white">Pagamento in verifica</h1>
              <p className="text-sm text-slate-400 mt-3 leading-relaxed">
                La conferma sta arrivando: controlla lo stato nella tua Area Clienti tra qualche minuto.
              </p>
            </div>
          )}
          <Link
            to="/area-clienti"
            data-testid="payment-area-link"
            className="inline-block mt-8 fire-bg text-white font-mono-adj text-xs uppercase tracking-[0.2em] font-semibold rounded-full px-8 py-4 hover:opacity-90 transition-opacity"
          >
            Vai all'Area Clienti
          </Link>
        </motion.div>
      </main>
    </div>
  );
}
